import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';

interface DistrictPerformanceChartProps {
  dateRange: 'week' | 'month' | 'quarter' | 'year';
  districtId: string;
}

interface PerformanceData {
  label: string;
  standardsCompleted: number;
  averageAccuracy: number;
  totalStudents: number;
}

const DistrictPerformanceChart: React.FC<DistrictPerformanceChartProps> = ({ dateRange, districtId }) => {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would call an API with the dateRange and districtId parameters
        // For now, we'll generate mock data based on the parameters
        const mockData = generateMockData(dateRange, districtId);
        setPerformanceData(mockData);
      } catch (error) {
        console.error('Error fetching district performance data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, districtId]);

  const generateMockData = (range: string, district: string): PerformanceData[] => {
    // Generate different data points based on the selected date range
    const dataPoints = range === 'week' ? 7 : range === 'month' ? 4 : range === 'quarter' ? 3 : 4;
    const result: PerformanceData[] = [];
    
    // Generate labels based on the date range
    const labels = generateLabels(range, dataPoints);
    
    // Base values that will be adjusted based on district and range
    const baseStandardsCompleted = district === 'all' ? 120 : 40;
    const baseAccuracy = 75;
    const baseTotalStudents = district === 'all' ? 5000 : 1500;
    
    // Generate data for each label
    for (let i = 0; i < dataPoints; i++) {
      // Add some variation to make the data look realistic
      const variation = Math.sin(i / dataPoints * Math.PI) * 20;
      const standardsCompleted = Math.round(baseStandardsCompleted + variation + (Math.random() * 20 - 10));
      const averageAccuracy = Math.min(100, Math.max(50, baseAccuracy + variation / 4 + (Math.random() * 5 - 2.5)));
      const totalStudents = Math.round(baseTotalStudents * (1 + Math.random() * 0.1 - 0.05));
      
      result.push({
        label: labels[i],
        standardsCompleted,
        averageAccuracy,
        totalStudents
      });
    }
    
    return result;
  };
  
  const generateLabels = (range: string, count: number): string[] => {
    const labels: string[] = [];
    
    switch (range) {
      case 'week':
        return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      case 'month':
        return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
      case 'quarter':
        return ['Month 1', 'Month 2', 'Month 3'];
      case 'year':
        return ['Q1', 'Q2', 'Q3', 'Q4'];
      default:
        return ['Period 1', 'Period 2', 'Period 3', 'Period 4'];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate max values for scaling the chart
  const maxStandardsCompleted = Math.max(...performanceData.map(d => d.standardsCompleted));
  const maxTotalStudents = Math.max(...performanceData.map(d => d.totalStudents));

  return (
    <div>
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">Standards Completed</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">Average Accuracy (%)</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
          <span className="text-sm text-gray-600">Active Students</span>
        </div>
      </div>
      
      <div className="relative h-64">
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500">
          {performanceData.map((data, index) => (
            <div key={index} className="text-center" style={{ width: `${100 / performanceData.length}%` }}>
              {data.label}
            </div>
          ))}
        </div>
        
        {/* Y-axis labels */}
        <div className="absolute top-0 bottom-8 left-0 flex flex-col justify-between text-xs text-gray-500">
          <div>100%</div>
          <div>75%</div>
          <div>50%</div>
          <div>25%</div>
          <div>0%</div>
        </div>
        
        {/* Chart area */}
        <div className="absolute top-0 bottom-8 left-8 right-0 flex items-end">
          {performanceData.map((data, index) => (
            <div key={index} className="flex flex-col items-center justify-end h-full" style={{ width: `${100 / performanceData.length}%` }}>
              {/* Standards Completed Bar */}
              <div 
                className="w-4 bg-blue-500 rounded-t"
                style={{ 
                  height: `${(data.standardsCompleted / maxStandardsCompleted) * 100}%`,
                  maxHeight: '90%'
                }}
                title={`Standards Completed: ${data.standardsCompleted}`}
              ></div>
              
              {/* Average Accuracy Line (positioned absolutely) */}
              <div 
                className="absolute w-full"
                style={{ 
                  bottom: `${data.averageAccuracy}%`,
                  left: `${index * (100 / performanceData.length)}%`,
                  width: `${100 / performanceData.length}%`
                }}
              >
                <div className="w-full flex justify-center">
                  <div 
                    className="h-1 w-4 bg-green-500 rounded"
                    title={`Average Accuracy: ${data.averageAccuracy.toFixed(1)}%`}
                  ></div>
                </div>
              </div>
              
              {/* Active Students Bar (positioned next to Standards Completed) */}
              <div 
                className="w-4 bg-purple-500 rounded-t ml-5"
                style={{ 
                  height: `${(data.totalStudents / maxTotalStudents) * 100}%`,
                  maxHeight: '90%'
                }}
                title={`Active Students: ${data.totalStudents}`}
              ></div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Total Standards Completed</div>
          <div className="text-xl font-bold text-gray-800">
            {performanceData.reduce((sum, data) => sum + data.standardsCompleted, 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Average Accuracy</div>
          <div className="text-xl font-bold text-gray-800">
            {(performanceData.reduce((sum, data) => sum + data.averageAccuracy, 0) / performanceData.length).toFixed(1)}%
          </div>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Total Active Students</div>
          <div className="text-xl font-bold text-gray-800">
            {performanceData[performanceData.length - 1]?.totalStudents.toLocaleString() || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistrictPerformanceChart;
